/* eslint-disable consistent-return */
import storage from '@/utils/storage';
import { filterFormParams } from '@/utils';
import BigNumber, { parseFen2Currency, Calculator } from '@/utils/number';
import getRandomId from '@/utils/uuid';
import DateJS from '@/utils/date';

export const isExistBlurFnByKey = (searchAttr, key) => {
  return !!searchAttr?.find((item) => item.key === key)?.componentProps?.onBlur;
};

export enum orderPageKey {
  reservation, // 预订房
  checkIn, // 入住房
  cancelReservation, // 预订取消
  checkOut, // 已退房
  roomOrderQuery, // 房间订单查询
  billQuery, // 账务查询
  frontDesk, // 增值业务
  frontGoods, // 商品直售
  memberCard, // 会员卡
  memberRecharge, // 会员充值
  selfService, // 自助点餐
  leaveVisit, // 离店回访
}

export enum filterKey {
  form, // 左右筛选
  searchForm, // 搜索项
  advancedSearch, // 高级筛选
}

interface ISavePageStatus {
  pageType: orderPageKey;
  formRef?: any;
  searchFormRef?: any;
  advancedSearchRef?: any;
  pageInfo?: any;
  callBack?: (params: any) => any;
}

// 获取页面滚动状态 和 表格的滚动状态(目前订单列表都只有一个表格)
export const getScrollStatus = () => {
  // 页面滚动状态
  try {
    const pageX =
      document.documentElement.scrollLeft || document.body.scrollLeft || window.pageXOffset;
    const pageY =
      document.documentElement.scrollTop || document.body.scrollTop || window.pageYOffset;

    // 表格滚动状态
    const tableBody = document.getElementsByClassName('ant-table-body')[0];
    const tableX = tableBody?.scrollLeft || 0;
    const tableY = tableBody?.scrollTop || 0;

    return {
      pageX,
      pageY,
      tableX,
      tableY,
    };
  } catch (error) {
    return {
      pageX: 0,
      pageY: 0,
      tableX: 0,
      tableY: 0,
    };
  }
};

// 滚动到指定位置
export const setScrollStatus = (scrollStatus: any) => {
  try {
    const { pageX, pageY, tableX, tableY } = scrollStatus || {};
    // 页面滚动
    window.scrollTo({ top: pageY, left: pageX });
    // 表格滚动
    const tableBody = document.getElementsByClassName('ant-table-body')?.[0];
    tableBody?.scrollTo({ top: tableY, left: tableX });
  } catch (error) {
    //
    console.log('scroll', error);
  }
};

export const orderLocalStorageKey = 'ORDER-LIST-STATUS';
// 列表跳入详情数据处理&存储
export const savePageStatus = ({ pageType, ...props }: ISavePageStatus) => {
  try {
    let scrollStatus = {};
    let pageSearchParams = {};

    switch (pageType) {
      case orderPageKey.reservation: // 预订房
        scrollStatus = getScrollStatus();
        pageSearchParams = {
          formRefValue: props?.formRef?.current?.getFieldsValue(),
          searchFormRefValue: props?.searchFormRef?.current?.getFieldsValue(),
          advancedSearchRefValue: props?.advancedSearchRef?.current?.getFieldsValue(),
        };
        storage.set(orderLocalStorageKey, JSON.stringify({ scrollStatus, pageSearchParams }));
        break;

      case orderPageKey.checkIn: // 入住房
        scrollStatus = getScrollStatus();
        pageSearchParams = {
          formRefValue: props?.formRef?.current?.getFieldsValue(),
          searchFormRefValue: props?.searchFormRef?.current?.getFieldsValue(),
          advancedSearchRefValue: props?.advancedSearchRef?.current?.getFieldsValue(),
        };
        storage.set(orderLocalStorageKey, JSON.stringify({ scrollStatus, pageSearchParams }));
        break;

      case orderPageKey.cancelReservation: // 预订取消
        scrollStatus = getScrollStatus();
        pageSearchParams = {
          formRefValue: props?.formRef?.current?.getFieldsValue(),
          searchFormRefValue: props?.searchFormRef?.current?.getFieldsValue(),
          advancedSearchRefValue: props?.advancedSearchRef?.current?.getFieldsValue(),
          pageInfo: props?.pageInfo,
        };
        storage.set(orderLocalStorageKey, JSON.stringify({ scrollStatus, pageSearchParams }));
        break;

      case orderPageKey.checkOut: // 已退房
        scrollStatus = getScrollStatus();
        pageSearchParams = {
          formRefValue: props?.formRef?.current?.getFieldsValue(),
          searchFormRefValue: props?.searchFormRef?.current?.getFieldsValue(),
          advancedSearchRefValue: props?.advancedSearchRef?.current?.getFieldsValue(),
          pageInfo: props?.pageInfo,
        };
        storage.set(orderLocalStorageKey, JSON.stringify({ scrollStatus, pageSearchParams }));
        break;

      case orderPageKey.roomOrderQuery: // 房间订单查询
      case orderPageKey.billQuery: // 账务查询
      case orderPageKey.leaveVisit: // 离店回访
        scrollStatus = getScrollStatus();
        pageSearchParams = {
          searchFormRefValue: props?.searchFormRef?.current?.getFieldsValue(),
          pageInfo: props?.pageInfo,
        };
        storage.set(orderLocalStorageKey, JSON.stringify({ scrollStatus, pageSearchParams }));
        break;
      case orderPageKey.frontDesk: // 增值业务
      case orderPageKey.frontGoods: // 前台商品
      case orderPageKey.memberCard: // 会员卡
      case orderPageKey.memberRecharge: // 会员充值
      case orderPageKey.selfService: // 自助点餐
        scrollStatus = getScrollStatus();
        pageSearchParams = {
          searchFormRefValue: props.searchFormRef,
          pageInfo: props.pageInfo,
        };
        storage.set(orderLocalStorageKey, JSON.stringify({ scrollStatus, pageSearchParams }));
        break;
      default:
        return null;
    }
  } catch (error) {
    //
  }
};

// 返回到详情页面的处理
export const dealPageStatus = ({ pageType, ...props }: ISavePageStatus) => {
  try {
    const { scrollStatus, pageSearchParams } = JSON.parse(
      storage.get(orderLocalStorageKey) || '{}',
    );
    // 处理滚动状态
    setScrollStatus(scrollStatus);

    const formRefValue = filterFormParams(pageSearchParams?.formRefValue);
    const searchFormRefValue = filterFormParams(pageSearchParams?.searchFormRefValue);
    const advancedSearchRefValue = filterFormParams(pageSearchParams?.advancedSearchRefValue);
    let type = null;
    let values = {};

    switch (pageType) {
      case orderPageKey.reservation: // 预订房
      case orderPageKey.checkIn: // 入住房
        // 筛选项赋值
        props?.formRef?.current?.setFieldsValue(pageSearchParams?.formRefValue);
        props?.searchFormRef?.current?.setFieldsValue(pageSearchParams?.searchFormRefValue);

        // 正常情况
        if (Object.keys(formRefValue).length > 0) {
          type = filterKey.form;
          values = formRefValue;
        }
        if (Object.keys(searchFormRefValue).length > 0) {
          type = filterKey.searchForm;
          values = searchFormRefValue;
        }
        if (Object.keys(advancedSearchRefValue).length > 0) {
          type = filterKey.advancedSearch;
          values = advancedSearchRefValue;
        }
        // 填写了搜索框的值 但是没有触发搜索
        if (Object.keys(formRefValue).length > 0 && Object.keys(searchFormRefValue).length > 0) {
          type = filterKey.form;
          values = formRefValue;
        }
        if (
          Object.keys(advancedSearchRefValue).length > 0 &&
          Object.keys(searchFormRefValue).length > 0
        ) {
          type = filterKey.advancedSearch;
          values = advancedSearchRefValue;
        }
        // 触发回调
        props?.callBack({ type, values }).then(() => {
          // 清空状态
          storage.delete(orderLocalStorageKey);
        });
        break;

      case orderPageKey.cancelReservation: // 预订取消
      case orderPageKey.checkOut: // 已退房
        // 筛选项赋值
        props?.formRef?.current?.setFieldsValue(pageSearchParams?.formRefValue);
        props?.searchFormRef?.current?.setFieldsValue(pageSearchParams?.searchFormRefValue);

        // 正常情况
        if (Object.keys(formRefValue).length > 0) {
          type = filterKey.form;
          values = formRefValue;
        }
        if (Object.keys(searchFormRefValue).length > 0) {
          type = filterKey.searchForm;
          values = searchFormRefValue;
        }
        if (Object.keys(advancedSearchRefValue).length > 0) {
          type = filterKey.advancedSearch;
          values = advancedSearchRefValue;
        }
        // 填写了搜索框的值 但是没有触发搜索
        if (Object.keys(formRefValue).length > 0 && Object.keys(searchFormRefValue).length > 0) {
          type = filterKey.form;
          values = formRefValue;
        }
        if (
          Object.keys(advancedSearchRefValue).length > 0 &&
          Object.keys(searchFormRefValue).length > 0
        ) {
          type = filterKey.advancedSearch;
          values = advancedSearchRefValue;
        }
        // 触发回调
        props?.callBack({ type, values, pagination: pageSearchParams?.pageInfo }).then(() => {
          // 清空状态
          storage.delete(orderLocalStorageKey);
        });
        break;
      case orderPageKey.roomOrderQuery: // 房间订单查询
      case orderPageKey.billQuery: // 账务查询
      case orderPageKey.leaveVisit: // 离店回访
        props?.formRef?.current?.setFieldsValue(pageSearchParams?.formRefValue);
        props?.searchFormRef?.current?.setFieldsValue(pageSearchParams?.searchFormRefValue);

        if (Object.keys(formRefValue).length > 0) {
          type = filterKey.form;
          values = formRefValue;
        }
        if (Object.keys(searchFormRefValue).length > 0) {
          type = filterKey.searchForm;
          values = searchFormRefValue;
        }
        // 触发回调
        props
          ?.callBack({
            type: filterKey.searchForm,
            values: searchFormRefValue,
            pagination: pageSearchParams?.pageInfo,
          })
          .then(() => {
            // 清空状态
            storage.delete(orderLocalStorageKey);
          });
        break;
      case orderPageKey.frontDesk: // 增值业务
      case orderPageKey.frontGoods: // 前台商品
      case orderPageKey.memberCard: // 会员卡
      case orderPageKey.memberRecharge: // 会员充值
      case orderPageKey.selfService: // 自助点餐
        // 触发回调
        props?.callBack({
          values: searchFormRefValue,
          type,
          pagination: pageSearchParams?.pageInfo,
        });
        storage.delete(orderLocalStorageKey);
        break;
      default:
        return null;
    }
  } catch (error) {
    //
  }
};

// 订单账务信息
export const formatOrderBillInfo = (data) => {
  const billListData = []; // 账务明细
  const crossNightData = []; // 跨夜审退款关联订单
  // 订单总金额
  const orderAmount = new Calculator();
  // 开票订单号
  let invoiceOrderNo = null;
  if (data) {
    (data.orderInfoList || []).forEach((item) => {
      const isRefund = item.orderType === '退款';
      const amountVal = item.amount || 0;
      if (isRefund) {
        // 退款
        orderAmount.minus(amountVal);
      } else {
        // 收款
        invoiceOrderNo = item.no;
        orderAmount.plus(amountVal);
      }
      billListData.push({
        ...item,
        isRefund,
        payAmountText: parseFen2Currency(isRefund ? -amountVal : amountVal, true),
        indexId: getRandomId(),
      });
    });
    (data.crossNightRefundInfoList || []).forEach((item) => {
      const amountVal = item.amount || 0;
      orderAmount.minus(amountVal);
      crossNightData.push({
        ...item,
        isRefund: true,
        refundAmountText: parseFen2Currency(-amountVal, true),
        indexId: getRandomId(),
      });
    });
  }
  return {
    billListData,
    crossNightData,
    // 订单金额为0时不允许开票
    invoiceOrderNo: new BigNumber(orderAmount.getValue()).isGreaterThan(0) ? invoiceOrderNo : null,
  };
};

// 列表页默认时间范围
export const generateDefaultTimeRange = () => {
  // 默认：3月前~今天
  return [DateJS().subtract(3, 'month'), DateJS()];
};

// 列表页最大时间范围
export const generateMaxTimeRange = () => {
  // 默认：3年前~今天
  return [DateJS().subtract(3, 'year'), DateJS()];
};
